import { useState, type FormEvent } from 'react';
import {
  ApiError,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  HelpTip,
  Input,
  formatDate,
} from '@dataroom/shared';
import {
  useConfirmRecoveryContact,
  useRecoveryContact,
  useRemoveRecoveryContact,
  useSetRecoveryContact,
} from '@/api/hooks';

function plainError(error: unknown): string | null {
  if (error instanceof ApiError) return error.message;
  return error ? 'Request failed.' : null;
}

/**
 * The firm's RECOVERY CONTACT (ADR-0055) on Settings — owner-only. An owner
 * enters an address; ClosingVault emails it a 6-digit code; the owner enters the
 * code (the address holder passes it on) and the address becomes the contact.
 * The contact is emailed about every owner change and recovery-contact change.
 * Asking for the current contact's address again cancels a pending change.
 */
export function RecoveryContactCard() {
  const contact = useRecoveryContact();
  const setContact = useSetRecoveryContact();
  const confirm = useConfirmRecoveryContact();
  const remove = useRemoveRecoveryContact();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [confirmRemove, setConfirmRemove] = useState(false);

  const data = contact.data;
  const error = plainError(setContact.error) ?? plainError(confirm.error) ?? plainError(remove.error);

  function resetErrors() {
    setContact.reset();
    confirm.reset();
    remove.reset();
  }

  function ask(e: FormEvent) {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    resetErrors();
    setContact.mutate(trimmed, { onSuccess: () => setEmail('') });
  }

  function enterCode(e: FormEvent) {
    e.preventDefault();
    const trimmed = code.trim();
    if (!trimmed) return;
    resetErrors();
    confirm.mutate(trimmed, { onSuccess: () => setCode('') });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Recovery contact <HelpTip id="admin.settings.recovery-contact" className="ml-1" />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          An email address outside your owners&apos; own mailboxes — for example a partner&apos;s
          personal address or your firm&apos;s legal contact. It is emailed whenever an owner is
          added or removed, and is where recovering your account would start if your firm ever lost
          access to every owner. It cannot sign in or see any data.
        </p>

        {contact.isPending ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : contact.error ? (
          <p className="text-sm text-destructive">{plainError(contact.error)}</p>
        ) : data ? (
          <>
            {data.email ? (
              <div className="flex flex-wrap items-center justify-between gap-2" aria-label="Current recovery contact">
                <p className="text-sm">
                  <span className="font-medium">{data.email}</span>
                  {data.confirmedAt ? (
                    <span className="text-muted-foreground"> · confirmed {formatDate(data.confirmedAt)}</span>
                  ) : null}
                </p>
                <button
                  type="button"
                  className="text-xs font-medium text-destructive hover:underline"
                  onClick={() => setConfirmRemove(true)}
                >
                  Remove
                </button>
              </div>
            ) : (
              <p className="text-sm">No recovery contact yet.</p>
            )}

            {data.pendingEmail ? (
              <form className="space-y-2 rounded-md border p-3" onSubmit={enterCode}>
                <p className="text-sm">
                  We emailed a 6-digit code to <span className="font-medium">{data.pendingEmail}</span>.
                  Enter it here to confirm.
                </p>
                <div className="flex gap-2">
                  <Input
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="123456"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    aria-label="Code"
                  />
                  <Button type="submit" disabled={confirm.isPending}>
                    {confirm.isPending ? 'Confirming…' : 'Confirm'}
                  </Button>
                </div>
                <div className="flex gap-4 text-xs">
                  <button
                    type="button"
                    className="font-medium text-primary hover:underline"
                    disabled={setContact.isPending}
                    onClick={() => {
                      resetErrors();
                      setContact.mutate(data.pendingEmail ?? '');
                    }}
                  >
                    Send a new code
                  </button>
                  {data.email ? (
                    <button
                      type="button"
                      className="font-medium text-muted-foreground hover:underline"
                      disabled={setContact.isPending}
                      onClick={() => {
                        resetErrors();
                        // Asking for the current contact again cancels the change.
                        setContact.mutate(data.email ?? '');
                      }}
                    >
                      Keep {data.email}
                    </button>
                  ) : null}
                </div>
              </form>
            ) : null}

            <form className="flex gap-2" onSubmit={ask}>
              <Input
                type="email"
                placeholder="name@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-label="Recovery contact email"
              />
              <Button type="submit" variant="outline" disabled={setContact.isPending}>
                {setContact.isPending ? 'Sending…' : data.email ? 'Change' : 'Send code'}
              </Button>
            </form>
          </>
        ) : null}

        {error ? <p className="text-sm text-destructive">{error}</p> : null}
      </CardContent>
      {confirmRemove && data?.email ? (
        <ConfirmDialog
          danger
          title={`Remove ${data.email} as your recovery contact?`}
          confirmLabel="Remove contact"
          pending={remove.isPending}
          onCancel={() => setConfirmRemove(false)}
          onConfirm={() => {
            resetErrors();
            remove.mutate(undefined, { onSettled: () => setConfirmRemove(false) });
          }}
        >
          <p>Every owner and {data.email} will be emailed about this change.</p>
        </ConfirmDialog>
      ) : null}
    </Card>
  );
}
